import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import { Clock, CheckCircle, XCircle, AlertCircle, Eye, HelpCircle, BookOpen, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const MyQuizHistory = () => {
  const navigate = useNavigate();
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API_URL}/my-quiz-history`);
      setAttempts(response.data);
    } catch (error) {
      toast.error('Error al cargar el historial');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (attempt) => {
    if (attempt.status === 'calificado' || attempt.status === 'completado') {
      return attempt.aprobado 
        ? <Badge className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" />Aprobado</Badge>
        : <Badge className="bg-red-100 text-red-800"><XCircle className="w-3 h-3 mr-1" />No aprobado</Badge>;
    }
    return <Badge className="bg-yellow-100 text-yellow-800"><AlertCircle className="w-3 h-3 mr-1" />En progreso</Badge>;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Calculate statistics
  const completedAttempts = attempts.filter(a => a.status !== 'en_progreso');
  const passedAttempts = attempts.filter(a => a.aprobado);
  const avgScore = completedAttempts.length > 0
    ? (completedAttempts.reduce((sum, a) => sum + a.porcentaje, 0) / completedAttempts.length).toFixed(1)
    : 0;

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#004E89]"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="font-manrope font-bold text-3xl text-slate-900">Historial de Evaluaciones</h1>
          <p className="font-inter text-slate-600 mt-1">Revisa todos tus intentos de quizzes</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-white rounded-lg border border-slate-200 p-4">
            <p className="text-sm text-slate-500">Total intentos</p>
            <p className="text-2xl font-bold text-slate-900">{attempts.length}</p>
          </div>
          <div className="bg-white rounded-lg border border-slate-200 p-4">
            <p className="text-sm text-slate-500">Completados</p>
            <p className="text-2xl font-bold text-slate-900">{completedAttempts.length}</p>
          </div>
          <div className="bg-white rounded-lg border border-slate-200 p-4">
            <p className="text-sm text-slate-500">Aprobados</p>
            <p className="text-2xl font-bold text-green-600">{passedAttempts.length}</p>
          </div>
          <div className="bg-white rounded-lg border border-slate-200 p-4">
            <p className="text-sm text-slate-500">Nota media</p>
            <p className="text-2xl font-bold text-slate-900">{avgScore}%</p>
          </div>
        </div>

        {/* Attempts List */}
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm">
          <div className="px-6 py-4 border-b border-slate-200">
            <h2 className="font-semibold">Todos los intentos</h2>
          </div>
          <div className="divide-y divide-slate-100">
            {attempts.map((attempt) => (
              <div key={attempt.id} className="p-4 hover:bg-slate-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      attempt.aprobado ? 'bg-green-100' : 'bg-slate-100'
                    }`}>
                      <HelpCircle className={`w-6 h-6 ${attempt.aprobado ? 'text-green-600' : 'text-slate-400'}`} />
                    </div>
                    <div>
                      <h3 className="font-medium text-slate-900">{attempt.quiz?.titulo}</h3>
                      <p className="text-sm text-slate-500">
                        {attempt.course?.nombre || 'Curso no disponible'}
                      </p>
                      <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(attempt.fecha_inicio)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className={`text-2xl font-bold ${attempt.aprobado ? 'text-green-600' : 'text-slate-900'}`}>
                        {attempt.porcentaje}%
                      </p>
                      <p className="text-sm text-slate-500">{attempt.puntuacion}/{attempt.puntuacion_maxima} pts</p>
                    </div>
                    {getStatusBadge(attempt)}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate(`/quiz-result/${attempt.id}`)}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
            {attempts.length === 0 && (
              <div className="p-12 text-center">
                <HelpCircle className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900">No hay intentos registrados</h3>
                <p className="text-slate-500 mt-1">Completa quizzes en tus cursos para ver tu historial</p>
                <Button onClick={() => navigate('/my-courses')} className="mt-4 bg-[#004E89] hover:bg-[#003B66]">
                  <BookOpen className="w-4 h-4 mr-2" /> Ir a mis cursos
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default MyQuizHistory;
